'use client';

import { useMemo, useState } from 'react';
import { classNames } from '../../utils/classNames';
import { buildSchedule, parseTime, resolveActiveCookTime } from './_buildSchedule';
import { MethodPicker } from './_MethodPicker';
import { PlanPanel } from './_PlanPanel';
import { ServingPicker } from './_ServingPicker';
import { StepOptionPicker } from './_StepOptionPicker';
import { TimePicker } from './_TimePicker';
import type { CookPlannerProps, UserSelectionStep } from './types';

export type { CookPlannerProps } from './types';

export default function CookPlanner({
  title,
  intro,
  theme = 'dark',
  protein,
  pullTempF,
  servingSectionLabel,
  servingTiers,
  defaultServingTier,
  methodSectionLabel,
  methods,
  serveTime,
  cookingSteps,
  planPanel,
  unsupportedMessage,
  disclaimer,
}: CookPlannerProps) {
  const isDark = theme === 'dark';

  const [selectedTierKey, setSelectedTierKey] = useState(defaultServingTier);
  const [selectedMethodIndex, setSelectedMethodIndex] = useState(0);
  const [stepSelections, setStepSelections] = useState<Record<number, number>>(() => {
    const defaults: Record<number, number> = {};
    cookingSteps.forEach((step, i) => {
      if (step.stepType === 'userSelection') {
        defaults[i] = (step.defaultOption ?? 1) - 1;
      }
    });
    return defaults;
  });
  const [serveTimeValue, setServeTimeValue] = useState(serveTime.defaultTime);

  const selectedTier = servingTiers.find((t) => t.key === selectedTierKey) ?? servingTiers[0];
  const selectedMethod = methods[selectedMethodIndex];
  const activeCookTime = useMemo(
    () => resolveActiveCookTime(selectedMethod, selectedTierKey, selectedTier),
    [selectedMethod, selectedTierKey, selectedTier],
  );

  const serveTimeMinutes = parseTime(serveTimeValue);

  const schedule = useMemo(
    () => buildSchedule(cookingSteps, stepSelections, selectedTier, activeCookTime, serveTimeValue),
    [cookingSteps, stepSelections, selectedTier, activeCookTime, serveTimeValue],
  );

  const handleStepSelect = (stepIndex: number, optionIndex: number) => {
    setStepSelections((prev) => ({ ...prev, [stepIndex]: optionIndex }));
  };

  return (
    <div
      className={classNames(
        'w-full px-4 py-12 md:px-8',
        isDark ? 'bg-neutral-900 text-white print:bg-warm-100 print:text-neutral-600' : 'bg-warm-100 text-neutral-600',
      )}
    >
      <div className="max-w-6xl mx-auto">
        <h1 className="tra-text-web-heading-2">{title}</h1>
        {intro && (
          <p
            className={classNames(
              'mt-5 max-w-xl text-sm',
              isDark ? 'text-neutral-100 print:text-neutral-600' : 'text-neutral-600',
            )}
          >
            {intro}
          </p>
        )}
        <div className="w-12 h-1 mt-5 bg-brand-300" aria-hidden="true" />

        <div className="grid grid-cols-1 gap-8 mt-10 md:grid-cols-2">
          {/* Left column — inputs */}
          <div className="flex flex-col gap-6">
            <ServingPicker
              label={servingSectionLabel ?? 'How many people are you serving'}
              tiers={servingTiers}
              selectedKey={selectedTierKey}
              onSelect={setSelectedTierKey}
              theme={theme}
            />

            <MethodPicker
              label={methodSectionLabel ?? 'Cooking method'}
              methods={methods}
              selectedIndex={selectedMethodIndex}
              tierKey={selectedTierKey}
              onSelect={setSelectedMethodIndex}
              theme={theme}
              labels={planPanel}
            />

            {cookingSteps.map((step, i) => {
              if (step.stepType !== 'userSelection') return null;
              return (
                <StepOptionPicker
                  key={i}
                  step={step as UserSelectionStep}
                  selectedIndex={stepSelections[i] ?? 0}
                  onSelect={(optIdx) => handleStepSelect(i, optIdx)}
                  theme={theme}
                />
              );
            })}

            <TimePicker
              config={serveTime}
              value={serveTimeValue}
              onChange={setServeTimeValue}
              theme={theme}
            />
          </div>

          {/* Right column — plan */}
          <PlanPanel
            tier={selectedTier}
            method={selectedMethod}
            cookTime={activeCookTime}
            pullTempF={pullTempF}
            schedule={schedule}
            serveTimeMinutes={serveTimeMinutes}
            labels={planPanel}
            protein={protein}
            unsupportedMessage={unsupportedMessage}
            theme={theme}
          />
        </div>
        {disclaimer && (
          <p
            className={classNames(
              'mt-8 text-xs',
              isDark ? 'text-neutral-200 print:text-neutral-500' : 'text-neutral-500',
            )}
          >
            {disclaimer}
          </p>
        )}
      </div>
    </div>
  );
}
